/**
 * EvidenciaDoAcesso — o último acesso de um veículo, com a foto e o recorte do mesmo quadro (Onda 191)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Usado no card de veículos de Associados e de Moradores. Mostra "Saída · 21/10/2026 13:10 · CAM02-Saída"
 * com a miniatura do recorte da placa; o clique abre a foto inteira e o recorte, juntos (regra da casa:
 * evidência sempre com recorte). A foto vem pela mesma rota da tela Saídas, que só exige login.
 *
 *   - veículo que nunca passou: "sem acesso registrado";
 *   - arquivo já apagado pela limpeza programada: "foto removida pela limpeza", com a data mantida.
 */
import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import PlacaVeicular from './PlacaVeicular';
import '../styles/EvidenciaDoAcesso.css';

function arquivo(rel, token) {
  return rel ? `/api/ocr-quality/file/${rel}?token=${encodeURIComponent(token || '')}` : null;
}
function dataHora(v) {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
const SENTIDO = { entrada: 'Entrada', saida: 'Saída' };

export function textoDoAcesso(a) {
  return [SENTIDO[a.log_type] || a.log_type, dataHora(a.detected_at), a.camera || `câmera ${a.camera_id}`]
    .filter(Boolean).join(' · ');
}

export default function EvidenciaDoAcesso({ acesso, placa }) {
  const auth = useAuth() || {};
  const token = auth.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  const [aberta, setAberta] = useState(false);
  const [semMiniatura, setSemMiniatura] = useState(false);

  if (!acesso) return <span className="ea-vazio">sem acesso registrado</span>;

  const recorte = arquivo(acesso.plate_crop_path, token);
  const texto = textoDoAcesso(acesso);
  return (
    <>
      <button type="button" className="ea-gatilho" title="Ver a foto e o recorte deste acesso"
        onClick={e => { e.stopPropagation(); setAberta(true); }}>
        {recorte && !semMiniatura
          ? <img className="ea-miniatura" src={recorte} alt={`Recorte da placa ${placa}`} loading="lazy" onError={() => setSemMiniatura(true)} />
          : <span className="ea-miniatura ea-miniatura--vazia">sem foto</span>}
        <span className="ea-texto">{texto}</span>
      </button>
      {aberta && <JanelaDaEvidencia acesso={acesso} placa={placa} texto={texto} token={token} onFechar={() => setAberta(false)} />}
    </>
  );
}

function JanelaDaEvidencia({ acesso, placa, texto, token, onFechar }) {
  const [faltaFoto, setFaltaFoto] = useState(!acesso.image_path);
  const [faltaRecorte, setFaltaRecorte] = useState(!acesso.plate_crop_path);
  useEffect(() => {
    const tecla = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onFechar]);
  return (
    <div className="ea-fundo" role="presentation" onClick={e => { e.stopPropagation(); onFechar(); }}>
      <div className="ea-janela" role="dialog" aria-label={`Último acesso de ${placa}`} onClick={e => e.stopPropagation()}>
        <div className="ea-cabecalho">
          <div>
            <PlacaVeicular placa={placa} tamanho="m" />
            <div className="ea-sub">Último acesso: {texto}</div>
          </div>
          <button type="button" className="ea-fechar" onClick={onFechar} aria-label="Fechar"><FiX /></button>
        </div>
        <div className="ea-fotos">
          {faltaFoto
            ? <div className="ea-foto ea-foto--vazia">foto removida pela limpeza</div>
            : <img className="ea-foto" src={arquivo(acesso.image_path, token)} alt={`Foto do acesso de ${placa}`} onError={() => setFaltaFoto(true)} />}
          {!faltaRecorte && (
            <img className="ea-recorte" src={arquivo(acesso.plate_crop_path, token)} alt={`Recorte da placa ${placa}`} onError={() => setFaltaRecorte(true)} />
          )}
        </div>
      </div>
    </div>
  );
}
